import { IonContent, IonPage } from './ionic';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { APP_NAME, isNative } from '../lib/platform';
import { useSession } from '../lib/useStore';
import { GearIcon, HomeIcon, PlugIcon, RecordIcon } from './icons';

/** A scrolling page. `tabs` adds the bottom navigation shown to signed-in users. */
export function Page({ children, tabs = false, wide = false }: { children: ReactNode; tabs?: boolean; wide?: boolean }) {
  return (
    <IonPage>
      <IonContent>
        <div className={`screen${wide ? ' screen--wide' : ''}`}>{children}</div>
      </IonContent>
      {tabs ? <TabBar /> : null}
    </IonPage>
  );
}

function TabBar() {
  const { pathname } = useLocation();
  // Connecting other products is for super admins only; nobody else sees the tab.
  const superAdmin = useSession().user?.isSuperAdmin === true;
  const item = (to: string, label: string, icon: ReactNode) => (
    <Link to={to} aria-current={pathname === to ? 'page' : undefined}>
      {icon}
      {label}
    </Link>
  );
  return (
    <nav className="tabbar" aria-label="Main">
      {item('/', 'Home', <HomeIcon />)}
      {item('/recordings', 'Recordings', <RecordIcon />)}
      {superAdmin ? item('/integrations', 'Integrations', <PlugIcon />) : null}
      {item('/settings', 'Settings', <GearIcon />)}
    </nav>
  );
}

export function Brand() {
  return (
    <Link to="/" className="brand">
      <img src={`${import.meta.env.BASE_URL}icon.svg`} alt="" width={28} height={28} />
      {APP_NAME}
    </Link>
  );
}

/** `deletion` adds the page on deleting an account, which is the website's only (the apps delete in Settings). */
export function LegalLinks({ deletion = false }: { deletion?: boolean }) {
  return (
    <span className="muted" style={{ fontSize: 13 }}>
      <Link to="/help" style={{ color: 'inherit' }}>
        How it works
      </Link>{' '}
      ·{' '}
      <Link to="/privacy" style={{ color: 'inherit' }}>
        Privacy policy
      </Link>{' '}
      ·{' '}
      <Link to="/terms" style={{ color: 'inherit' }}>
        Terms
      </Link>
      {deletion && !isNative ? (
        <>
          {' '}
          ·{' '}
          <Link to="/delete-account" style={{ color: 'inherit' }}>
            Delete your account
          </Link>
        </>
      ) : null}
    </span>
  );
}

/**
 * What someone accepts by signing in, or by joining a meeting: the Terms,
 * having read the Privacy policy. Under every way to sign in and every
 * "Ask to join" (Google Play's user-generated content policy and App Store
 * guideline 1.2 want the terms accepted before anyone can post).
 */
export function AgreeLine({ action }: { action: 'continuing' | 'joining' }) {
  return (
    <p className="agree" data-testid="agree">
      By {action}, you agree to the <Link to="/terms">Terms</Link> and have read the <Link to="/privacy">Privacy policy</Link>.
    </p>
  );
}
